// COMPONENTS & FUNCTIONS
import {
  calculateHandler,
  convertHandler,
} from "../../functions/mathHandler";
import {
  createTrackingValues,
  GraphLegendResult,
  validateGraphControls,
} from "./graph";

// INTERFACES
import {
  BaseQuantity,
  GraphControls,
  GraphLegend,
} from "../../interface/functionalityInterface";
import { CalculatorUnitProps } from "../../interface/propTypesInterface";

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const containsVariable = (
  expression: string,
  name: string
) => new RegExp(`\\b${escapeRegExp(name)}\\b`).test(expression);

const replaceVariable = (
  expression: string,
  name: string,
  value: number
) =>
  expression.replace(
    new RegExp(`\\b${escapeRegExp(name)}\\b`, "g"),
    `(${value})`
  );

const toBaseUnit = (
  quantity: BaseQuantity,
  value: string | number
) =>
  convertHandler(
    value.toString().replaceAll("'", "").replace(",", "."),
    quantity.baseUnit,
    quantity.selectedUnit,
    quantity.units
  );

const fromBaseUnit = (
  quantity: BaseQuantity,
  value: number
) =>
  convertHandler(
    value.toString(),
    quantity.selectedUnit,
    quantity.baseUnit,
    quantity.units
  );

export const calculateCalculatorGraph = (
  calculator: CalculatorUnitProps,
  legend: GraphLegend,
  controls: GraphControls
): GraphLegendResult => {
  const controlsError = validateGraphControls(controls);
  if (controlsError) {
    return { legendId: legend.id, points: [], error: controlsError };
  }

  const mode = calculator.inputs[calculator.selectedCalcMode];
  const equations =
    calculator.calculate[calculator.selectedCalcMode]?.equations;
  const equation = equations?.find(
    (candidate) => candidate.target === legend.yKey
  );
  const xQuantity = mode.baseQuantities.find(
    (quantity) => quantity.name === legend.xKey
  );
  const yQuantity = mode.baseQuantities.find(
    (quantity) => quantity.name === legend.yKey
  );

  if (!equation || !xQuantity || !yQuantity) {
    return {
      legendId: legend.id,
      points: [],
      error: "Select a valid X input and Y result.",
    };
  }
  if (legend.xKey === legend.yKey) {
    return {
      legendId: legend.id,
      points: [],
      error: "X and Y must be different variables.",
    };
  }
  if (!containsVariable(equation.expression, legend.xKey)) {
    return {
      legendId: legend.id,
      points: [],
      error: `${legend.xKey.replaceAll(
        "_",
        " "
      )} is not used by the selected Y equation.`,
    };
  }
  if (containsVariable(equation.expression, legend.yKey)) {
    return {
      legendId: legend.id,
      points: [],
      error: "The selected equation depends on its own result.",
    };
  }

  let preparedExpression = equation.expression;
  const missingConstants: string[] = [];
  mode.baseQuantities.forEach((quantity) => {
    if (
      quantity.name === legend.xKey ||
      quantity.name === legend.yKey ||
      !containsVariable(preparedExpression, quantity.name)
    ) {
      return;
    }
    if (quantity.value === "") {
      missingConstants.push(quantity.name.replaceAll("_", " "));
      return;
    }
    try {
      preparedExpression = replaceVariable(
        preparedExpression,
        quantity.name,
        toBaseUnit(quantity, quantity.value)
      );
    } catch {
      missingConstants.push(quantity.name.replaceAll("_", " "));
    }
  });

  if (missingConstants.length > 0) {
    return {
      legendId: legend.id,
      points: [],
      error: `Set calculator constants: ${missingConstants.join(", ")}.`,
    };
  }

  const trackingValues = createTrackingValues(controls);
  if (!trackingValues) {
    return {
      legendId: legend.id,
      points: [],
      error:
        "Adjust the cursor increment to create between 2 and 1000 tracking points.",
    };
  }

  const points = trackingValues.map((x) => {
    try {
      const expression = replaceVariable(
        preparedExpression,
        legend.xKey,
        toBaseUnit(xQuantity, x)
      );
      const baseResult = calculateHandler(expression);
      const y =
        baseResult === null || !Number.isFinite(baseResult)
          ? null
          : fromBaseUnit(yQuantity, baseResult);
      return { x, y };
    } catch {
      return { x, y: null };
    }
  });

  if (points.every((point) => point.y === null)) {
    return {
      legendId: legend.id,
      points,
      error: "The selected range produced no valid Y values.",
    };
  }

  return { legendId: legend.id, points };
};
